import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AlertCircle, Info, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { logger } from "@/utils/logger";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  detachDocumentVersion,
  fetchLineageDetachable,
  fetchLineageEditable,
  fetchLineageRows,
  isVersionLineageEditAvailable,
  isVersionLineageFailure,
  reorderDocumentVersions,
  type DetachVersionResult,
  type VersionLineageOutcome,
} from "@/stores/documents/repository";
import { canPreviewFile } from "@/utils/previewUtils";
import { isDwgParentDoc } from "@/domains/documents/hooks/useDrawingViewer";
import { notifyPinnedDocuments } from "../../services/pinnedDocuments";
import { getVersionHistory } from "../../services/versionService";
import {
  lineageDetachableKey,
  lineageEditableKey,
  lineageRowsKey,
  lineageStatsKey,
  versionHistoryKey,
} from "../../types/addVersions";
import {
  hasVersioningChanges,
  planVersioning,
  projectVersionNumbers,
} from "../../utils/versioningPlan";
import { VersioningRow } from "../molecules/VersioningRow";

export interface VersioningTarget {
  id: string;
  filename: string;
  versionGroup: string | null;
}

interface VersioningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: VersioningTarget | null;
  /** Switch to the "Add new versions" dialog for the same document. */
  onAddVersions?: () => void;
  /** The document turned out to have one live version (its older ones are in
   *  the bin): there is nothing to arrange, so the host shows "Add new
   *  version" in this dialog's place. Only on opening, never after a save. */
  onNothingToArrange?: () => void;
  /** A save changed these lineages; the host drops caches and re-reads. */
  onEdited: (versionGroups: string[]) => void;
  /** Open one version in the host's viewer — the handler the version history
   *  uses. Without it the rows have no preview button. */
  onPreview?: (documentId: string, filePath: string, filename: string) => void;
  /** Where a successful save goes instead of closing — the host passes it
   *  while "Add new versions" still holds staged files, so closing (which
   *  clears the target) cannot drop them. Says where the target went when
   *  the save made it a separate file, so those files follow it. */
  onSaved?: (saved: { targetDetached: DetachVersionResult | null }) => void;
  /** Files staged in "Add new versions" that closing would drop with the target. */
  hasStagedFiles?: boolean;
}

/**
 * "Versioning" — shown instead of "Add new version" for a document that
 * already has versions. Lists the live versions newest first, the current
 * one on top; the user can drag them into a new order (the top one becomes
 * current), preview any of them, mark versions to become separate files, or
 * go on to add more. Internally the order is kept oldest first, the shape the
 * reorder RPC takes; only the rendering is reversed.
 *
 * Edits are staged and applied together on Save: the separations first, one
 * at a time, then one reorder of what stays. Each badge shows the number the
 * row will carry afterwards — the same arithmetic the database does.
 */
// observer: the permission grants are MobX state that can arrive after open.
export const VersioningDialog = observer(function VersioningDialog({
  open,
  onOpenChange,
  target,
  onAddVersions,
  onNothingToArrange,
  onEdited,
  onPreview,
  onSaved,
  hasStagedFiles = false,
}: VersioningDialogProps) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const perms = usePermissions();
  const copy = t.documents.versioning.manage;
  const versionGroup = target?.versionGroup ?? null;
  const hasTarget = target !== null;

  const history = useQuery({
    queryKey: versionHistoryKey(versionGroup ?? ""),
    queryFn: () => getVersionHistory(versionGroup ?? "", { requireComplete: true }),
    enabled: open && versionGroup !== null,
    staleTime: 0,
  });
  const lineage = useQuery({
    queryKey: lineageRowsKey(versionGroup ?? ""),
    queryFn: () => fetchLineageRows(versionGroup ?? ""),
    enabled: open && versionGroup !== null,
    staleTime: 0,
  });
  const editableCheck = useQuery({
    queryKey: lineageEditableKey(versionGroup ?? ""),
    queryFn: () => fetchLineageEditable(versionGroup ?? ""),
    enabled: open && versionGroup !== null,
    staleTime: 0,
  });
  // Detach also renumbers the trashed versions it leaves behind, so it can be
  // refused where a reorder is allowed; asked separately so each control is
  // offered on its own answer.
  const detachableCheck = useQuery({
    queryKey: lineageDetachableKey(versionGroup ?? ""),
    queryFn: () => fetchLineageDetachable(versionGroup ?? ""),
    enabled: open && versionGroup !== null,
    staleTime: 0,
  });

  // Oldest first, as the reorder RPC takes them; rendered reversed. Sorted
  // here, totally, in the database's election order (version, NULL lowest,
  // then id): the history read orders by version alone, so legacy duplicate
  // or NULL numbers could otherwise arrive in a different order on a refetch
  // — offering a Save nobody asked for — or put a NULL row in the head's place.
  const versions = useMemo(
    () =>
      [...(history.data ?? [])].sort(
        (a, b) => (a.version ?? -Infinity) - (b.version ?? -Infinity) || a.id - b.id
      ),
    [history.data]
  );
  const original = useMemo(() => versions.map((v) => v.id), [versions]);

  const [order, setOrder] = useState<number[] | null>(null);
  const [separated, setSeparated] = useState<Set<number>>(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);

  // Edits belong to one opening of the dialog for one document. A background
  // refetch must not throw them away — `currentOrder` already folds in rows
  // the list gained or lost — so only opening, closing, a new target or a
  // finished save (`resetEdits`) clears them.
  const resetEdits = () => {
    setOrder(null);
    setSeparated(new Set());
  };
  useEffect(resetEdits, [open, versionGroup]);

  // The entry point goes by the head's version number, which a lineage whose
  // older versions were all binned still carries. Judge by what loaded, once
  // per opening: a save that leaves one version stays here to show it. Only
  // a read made since this opening counts — a list cached from an earlier one
  // may predate an add or a trash, and would hand over (or not) on old facts.
  // "Since this opening" is counted, not timed: each read's update counters
  // are noted when the opening renders, and a read is this opening's once its
  // counter has moved past that. A millisecond clock cannot say it — a cache
  // write in the opening's own millisecond passed as fresh. Noted while
  // rendering, so the very first frame already judges a cached list by them.
  const openKey = open ? versionGroup : null;
  const queryKeys = [
    versionHistoryKey(versionGroup ?? ""),
    lineageRowsKey(versionGroup ?? ""),
    lineageEditableKey(versionGroup ?? ""),
    lineageDetachableKey(versionGroup ?? ""),
  ];
  const updateCounts = () =>
    queryKeys.map((key) => {
      const state = queryClient.getQueryState(key);
      return { data: state?.dataUpdateCount ?? 0, error: state?.errorUpdateCount ?? 0 };
    });
  const [opening, setOpening] = useState(() => ({ key: openKey, baseline: updateCounts() }));
  if (opening.key !== openKey) setOpening({ key: openKey, baseline: updateCounts() });
  const current = updateCounts();
  /** Has query `i` (in `queryKeys` order) delivered data / an error since this opening? */
  const readThisOpening = (i: number) => current[i].data > opening.baseline[i].data;
  const failedThisOpening = (i: number) => current[i].error > opening.baseline[i].error;
  const historyReadThisOpening = readThisOpening(0);
  const openingChecked = useRef(false);
  useEffect(() => {
    openingChecked.current = false;
  }, [open, versionGroup]);
  useEffect(() => {
    // No lineage to arrange at all: hand over rather than show an empty list.
    if (open && hasTarget && versionGroup === null) onNothingToArrange?.();
  }, [open, hasTarget, versionGroup, onNothingToArrange]);
  useEffect(() => {
    if (!open || openingChecked.current || !history.isSuccess || history.isFetching) return;
    if (!historyReadThisOpening) return;
    openingChecked.current = true;
    if (history.data.length < 2) onNothingToArrange?.();
  }, [
    open,
    history.isSuccess,
    history.isFetching,
    history.data,
    historyReadThisOpening,
    onNothingToArrange,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const currentOrder = useMemo(() => {
    if (!order) return original;
    // The ids the user arranged, minus any the list lost since, fill the slots
    // they hold in the fresh list. A row the list gained (a version restored
    // from the bin while the dialog was open) keeps its own slot there — where
    // the database already places it — so it neither looks like an edit nor
    // gets renumbered by one.
    const arranged = order.filter((id) => original.includes(id));
    const arrangedSet = new Set(arranged);
    let next = 0;
    return original.map((id) => (arrangedSet.has(id) ? arranged[next++] : id));
  }, [order, original]);
  // Newest on top.
  const displayOrder = useMemo(() => [...currentOrder].reverse(), [currentOrder]);
  const byId = useMemo(() => new Map(versions.map((v) => [v.id, v])), [versions]);
  const plan = planVersioning(original, currentOrder, separated);
  const dirty = hasVersioningChanges(plan);
  const projected = useMemo(
    () => projectVersionNumbers(lineage.data ?? [], currentOrder, separated),
    [lineage.data, currentOrder, separated]
  );
  const kept = currentOrder.filter((id) => !separated.has(id));
  // Marks survive a refetch, so a version trashed elsewhere meanwhile can
  // leave none kept; the server would refuse the last detach midway.
  const keepsOne = kept.length > 0;
  const newHeadId = kept[kept.length - 1] ?? null;
  const oldHeadId =
    versions.find((v) => v.is_latest_version === true)?.id ?? original[original.length - 1];

  // The RPCs refuse a caller who cannot modify every live row where it is
  // filed, so the controls are only offered to one who can. A project's
  // files are gated on the project's own grant (which per-project member
  // overrides feed), never on the org-wide one; only a row filed outside any
  // project falls back to MODIFY_DATA. Folder denials are left to the server.
  // Fails closed: no live row read means nothing to grant edits on. RLS hides
  // versions in projects the caller is not on, which no client read can see,
  // so the server's own answer (`document_lineage_editable`) is required too.
  const liveLineage = lineage.data?.filter((row) => !row.isDeleted) ?? [];
  // Grants read false until the org's permissions have loaded: until then
  // the dialog is still loading, not refusing.
  const permsReady = perms.isRbacReady === true;
  const canEdit =
    permsReady &&
    editableCheck.data === true &&
    liveLineage.length > 0 &&
    liveLineage.every((row) =>
      row.projectId === null ? perms.canModifyData : perms.canModifyFilesInProject(row.projectId)
    );
  const editable = canEdit && isVersionLineageEditAvailable() && !isSaving;
  const canDetach = detachableCheck.data === true;

  // Moves one kept row to where another kept row is. Rows marked to become
  // separate hold their places — so "Keep as version" puts one back where
  // the user last saw it — and the kept rows flow around them.
  const moveKept = (fromId: number, toId: number) => {
    const keptIds = currentOrder.filter((id) => !separated.has(id));
    const from = keptIds.indexOf(fromId);
    const to = keptIds.indexOf(toId);
    // -1 when a refetch dropped a row mid-drag, or the target is separate.
    if (from < 0 || to < 0 || from === to) return;
    const moved = arrayMove(keptIds, from, to);
    let next = 0;
    setOrder(currentOrder.map((id) => (separated.has(id) ? id : moved[next++])));
  };

  // The arrows step over rows marked to become separate: swapping with one
  // would change nothing that is saved.
  const neighbourKept = (index: number, direction: -1 | 1) => {
    for (let i = index + direction; i >= 0 && i < currentOrder.length; i += direction) {
      if (!separated.has(currentOrder[i])) return i;
    }
    return -1;
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    moveKept(Number(active.id), Number(over.id));
  };

  const toggleSeparate = (id: number) =>
    setSeparated((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const refresh = () => {
    if (!versionGroup) return;
    void queryClient.invalidateQueries({ queryKey: versionHistoryKey(versionGroup) });
    void queryClient.invalidateQueries({ queryKey: lineageRowsKey(versionGroup) });
    void queryClient.invalidateQueries({ queryKey: lineageStatsKey(versionGroup) });
    void queryClient.invalidateQueries({ queryKey: lineageEditableKey(versionGroup) });
    void queryClient.invalidateQueries({ queryKey: lineageDetachableKey(versionGroup) });
  };

  const reportFailure = (outcome: VersionLineageOutcome<unknown>) => {
    if (!isVersionLineageFailure(outcome)) return;
    if (outcome.pinned) {
      notifyPinnedDocuments([outcome.pinned]);
      return;
    }
    toast({
      variant: "destructive",
      title: t.common.error,
      description: copy.errors[outcome.reason],
    });
  };

  const handleSave = async () => {
    if (isSaving || !versionGroup || !dirty || !target || !keepsOne) return;
    setIsSaving(true);
    const touched = new Set<string>([versionGroup]);
    const renames: Array<{ from: string; to: string }> = [];
    let detached = 0;
    let failed = false;
    let didAnything = false;
    // A request that failed without a verdict from the database (transport
    // error, unreadable reply) may still have committed, so the host's list
    // must revalidate as if it had.
    let uncertain = false;
    // Set while a call is awaiting the database: only a throw then can
    // stand for a write that may have landed.
    let inFlight = false;
    let targetDetached: DetachVersionResult | null = null;

    try {
      // One at a time: each detach renumbers the lineage under its lock.
      for (const id of plan.detach) {
        inFlight = true;
        const outcome = await detachDocumentVersion({ documentId: id });
        inFlight = false;
        if (isVersionLineageFailure(outcome)) {
          reportFailure(outcome);
          failed = true;
          if (outcome.indeterminate) uncertain = true;
          break;
        }
        didAnything = true;
        detached += 1;
        if (String(id) === target.id) targetDetached = outcome.result;
        touched.add(outcome.result.versionGroup);
        if (outcome.result.renamed) renames.push(outcome.result.renamed);
        if (outcome.result.sourceHeadRenamed) renames.push(outcome.result.sourceHeadRenamed);
      }

      let reordered = false;
      if (!failed && plan.reorder) {
        inFlight = true;
        const outcome = await reorderDocumentVersions({
          versionGroup,
          documentIds: plan.reorder,
        });
        inFlight = false;
        if (isVersionLineageFailure(outcome)) {
          reportFailure(outcome);
          failed = true;
          if (outcome.indeterminate) uncertain = true;
        } else {
          didAnything = true;
          reordered = true;
          if (outcome.result.headRenamed) renames.push(outcome.result.headRenamed);
        }
      }

      if (didAnything) {
        if (failed) {
          toast({ variant: "destructive", title: copy.toastPartialTitle });
        } else {
          toast({
            title: copy.toastSavedTitle,
            description: [
              reordered
                ? copy.toastReorderedDescription.replace("{filename}", target.filename)
                : null,
              detached > 0
                ? copy.toastDetachedDescription.replace("{count}", String(detached))
                : null,
            ]
              .filter(Boolean)
              .join(" "),
          });
        }
        for (const rename of renames) {
          toast({
            title: copy.toastRenamedTitle,
            description: copy.toastRenamedDescription
              .replace("{from}", rename.from)
              .replace("{to}", rename.to),
          });
        }
        onEdited([...touched]);
      } else if (uncertain) {
        onEdited([...touched]);
      }
    } catch (error) {
      // The calls report RPC failures as outcomes; a throw during one is the
      // transport, and may have committed. One anywhere else applied nothing.
      failed = true;
      if (inFlight) uncertain = true;
      void logger.error("data", "versioning-save", "Versioning save threw", error as Error, {
        versionGroup,
        detached,
      });
      toast({
        variant: "destructive",
        title: didAnything ? copy.toastPartialTitle : t.common.error,
        description: copy.errors.unknown,
      });
      // Only a write that landed, or may have, changed anything to re-read.
      if (didAnything || uncertain) onEdited([...touched]);
    } finally {
      setIsSaving(false);
      // Once anything applied, the edits were made on a list that is now
      // stale. When nothing did, keep them: the dialog stays open on the same
      // list, and the user should not have to redo every drag to retry —
      // unless the database turned out not to have the edits at all, when a
      // retry can never succeed and the banner says so.
      // A call that may have committed counts as applied: retrying the same
      // edits on top of it would act on a lineage that has moved on.
      if (didAnything || uncertain || !isVersionLineageEditAvailable()) resetEdits();
      refresh();
    }

    // Done: close, or go where the host asked. After a failure stay, on the
    // reloaded list, so the user sees what actually happened.
    if (!failed) {
      if (onSaved) onSaved({ targetDetached });
      else onOpenChange(false);
    }
  };

  if (!target) return null;

  // A failed background refetch keeps what is on screen, once this opening
  // has read it; a list cached from an earlier opening is no read at all, and
  // its failed refresh is a failure to show, not an old list to edit. The
  // edit check counts: a failed one is not an answer, and must not read as
  // "no access".
  // Same order as `queryKeys`.
  const queries = [history, lineage, editableCheck, detachableCheck];
  const loadFailed = queries.some(
    (query, i) =>
      query.isError && failedThisOpening(i) && (query.data === undefined || !readThisOpening(i))
  );
  // Until every read has answered for this opening, there is nothing to edit:
  // the edit check gates the controls (without it the rows would read as a
  // read-only lineage with no note saying why), and a list cached by an
  // earlier opening may predate a change the save would act on blindly.
  // A target with no lineage has nothing to read: its queries never run, so
  // waiting on them would load forever when no host takes over.
  const loading =
    versionGroup !== null &&
    !loadFailed &&
    (!permsReady ||
      queries.some((query, i) => !readThisOpening(i) && !(query.isError && failedThisOpening(i))));

  // Escape, the overlay or Cancel would throw away the arrangement — and any
  // files staged for adding, which go with the target — so ask first.
  const requestClose = () => {
    if (isSaving) return;
    if (dirty || hasStagedFiles) setConfirmingClose(true);
    else onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{copy.dialogTitle}</DialogTitle>
          <DialogDescription>
            {copy.dialogDescription.replace("{filename}", target.filename)}
          </DialogDescription>
        </DialogHeader>

        {!isVersionLineageEditAvailable() ? (
          <p className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            {copy.editUnavailable}
          </p>
        ) : (
          !loading &&
          permsReady &&
          lineage.data !== undefined &&
          editableCheck.data !== undefined &&
          !canEdit && (
            <p className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              {copy.editNotPermitted}
            </p>
          )
        )}

        {loading ? (
          <div className="space-y-2" aria-label={copy.loading}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : loadFailed ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center text-sm">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p>{copy.loadError}</p>
            <Button type="button" variant="outline" size="sm" onClick={refresh}>
              {copy.retry}
            </Button>
          </div>
        ) : (
          <TooltipProvider>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={displayOrder} strategy={verticalListSortingStrategy}>
                <ul className="max-h-[min(36rem,60vh)] space-y-1.5 overflow-y-auto">
                  {displayOrder.map((id) => {
                    const version = byId.get(id);
                    if (!version) return null;
                    const isSeparated = separated.has(id);
                    // Index into the oldest-first order: up on screen is newer.
                    const index = currentOrder.indexOf(id);
                    return (
                      <VersioningRow
                        key={id}
                        version={version}
                        copy={copy}
                        projectedVersion={isSeparated ? null : (projected.get(id) ?? null)}
                        currentLabel={
                          // Unedited, the badge names the row the database
                          // elected, even if another sorts on top: nothing
                          // pending may be claimed that Save cannot apply.
                          !dirty
                            ? id === oldHeadId
                              ? "current"
                              : null
                            : id !== newHeadId
                              ? null
                              : id === oldHeadId
                                ? "current"
                                : "becomes"
                        }
                        separated={isSeparated}
                        isFirst={neighbourKept(index, 1) < 0}
                        isLast={neighbourKept(index, -1) < 0}
                        editable={editable}
                        separateDisabledReason={
                          isSeparated
                            ? null
                            : !canDetach
                              ? copy.separateNotPermitted
                              : kept.length <= 1
                                ? copy.lastRemainingHint
                                : null
                        }
                        onMove={(direction) =>
                          moveKept(
                            id,
                            currentOrder[neighbourKept(index, direction === 1 ? -1 : 1)] ?? -1
                          )
                        }
                        onToggleSeparate={() => toggleSeparate(id)}
                        onPreview={
                          onPreview && !isDwgParentDoc(version) && canPreviewFile(version.filename)
                            ? () =>
                                onPreview(String(version.id), version.file_path, version.filename)
                            : undefined
                        }
                      />
                    );
                  })}
                </ul>
              </SortableContext>
            </DndContext>
          </TooltipProvider>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {onAddVersions ? (
            <Button
              type="button"
              variant="outline"
              onClick={onAddVersions}
              disabled={isSaving || dirty}
            >
              <Plus className="mr-2 h-4 w-4" />
              {copy.addVersions}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={requestClose} disabled={isSaving}>
              {t.common.cancel}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSave()}
              disabled={!editable || !dirty || !keepsOne || loading || loadFailed}
            >
              {isSaving ? copy.saving : dirty ? copy.save : copy.noChanges}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      <AlertDialog open={confirmingClose} onOpenChange={setConfirmingClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.discardTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {hasStagedFiles ? copy.discardWithStagedDescription : copy.discardDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{copy.keepEditing}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmingClose(false);
                onOpenChange(false);
              }}
            >
              {copy.discard}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
});
